import { Card, CardContent, Control, ControlError, ControlHelper, Input, Label, Select, SelectOption, PageLoader, CircularLoader, ContentLoader } from '@forge/common'
import React from 'react'
import { useTranslation } from 'react-i18next';
import { LOCALE_CONST } from '../../../utils/constants';

export interface HardwareDetailsProps {
    hardwareData: {
        deviceType: string;
        hardwareModel: string;
        hardwareID: string;
        publicKey?: string;
        encryptionKey?: string;
    };
    errorState: {
        showDeviceTypeError: boolean;
        showHardwareModelError: boolean;
        showHardwareIDError: boolean;
        showValidHardwareIDError: boolean;
        showHardwareModelTypeError: boolean;
        showPublicKeyError?: boolean;
        showEncryptionKeyError?: boolean;
    };
    deviceTypeOptions?: string[];
    onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, type: string) => void;
    isLoading?: boolean;
    isEditMode?: boolean;
    showPublicKey?: boolean;
    keyFieldLabel?: string;
    keyFieldType?: "publicKey" | "encryptionKey";
}

const HardwareDetails: React.FC<HardwareDetailsProps> = ({
    hardwareData,
    errorState,
    deviceTypeOptions = [],
    onInputChange,
    isLoading = false,
    isEditMode = false,
    showPublicKey = false,
    keyFieldLabel = LOCALE_CONST.publicKey,
    keyFieldType = "publicKey",
}) => {
    const { t } = useTranslation();
    
    const hasHardwareModelError = errorState.showHardwareModelError || errorState.showHardwareModelTypeError;
    const hasHardwareIDError = errorState.showHardwareIDError || errorState.showValidHardwareIDError;

    return (
        <>
            <Card className="mx-auto mt-6 w-[25rem] min-w-full max-w-2xl edit-dev-card-block-cls">
                <span className="iot-basic-info-text p-5 block">
                    {t(LOCALE_CONST.hardwareDetails)}
                </span>
                {isLoading ? <ContentLoader/> :
                    <CardContent top="none">
                        <Control className="w-[50%] mt-2">
                            <Label
                                className="font-normal"
                                required
                                requiredText=""
                            >
                                {t(LOCALE_CONST.deviceType)}
                            </Label>
                            {isEditMode ? (
                                <Input
                                    aria-label="Device Type"
                                    value={hardwareData.deviceType}
                                    id="deviceType"
                                    size="regular"
                                    disabled
                                    data-testid="test-device-type"
                                />
                            ) : (
                                <Select
                                    aria-label="Device Type"
                                    value={hardwareData.deviceType}
                                    height={5}
                                    id="deviceType"
                                    size="regular"
                                    onChange={(e) => onInputChange(e, "deviceType")}
                                    error={errorState.showDeviceTypeError}
                                    data-testid="test-device-type"
                                >
                                    {deviceTypeOptions.map((option) => (
                                        <SelectOption
                                            key={option}
                                            label={option}
                                            value={option}
                                        />
                                    ))}
                                </Select>
                            )}
                            {errorState.showDeviceTypeError && (
                                <ControlError showIcon>{t(LOCALE_CONST.requiredField)}</ControlError>
                            )}
                        </Control>

                        <Control className="w-[50%]">
                            <Label className="font-normal" required requiredText="">
                                {t(LOCALE_CONST.hardwareModel)}
                            </Label>
                            <Input
                                placeholder="#######"
                                size="regular"
                                id='hardwareModel'
                                value={hardwareData.hardwareModel}
                                onChange={(e) => onInputChange(e, "hardwareModel")}
                                error={hasHardwareModelError}
                                data-testid="test-hardware-model"
                                maxLength={255}
                            />
                            {errorState.showHardwareModelTypeError && (
                                <ControlError showIcon>{t(LOCALE_CONST.invalidCharacter)}</ControlError>
                            )}
                            {errorState.showHardwareModelError && (
                                <ControlError showIcon>{t(LOCALE_CONST.requiredField)}</ControlError>
                            )}
                        </Control>

                        <Control className="w-[50%]">
                            <Label className="font-normal" required requiredText="">
                                {t(LOCALE_CONST.hardwareID)}
                            </Label>
                            <Input
                                placeholder="#######"
                                size="regular"
                                id='hardwareID'
                                value={hardwareData.hardwareID}
                                onChange={(e) => onInputChange(e, "hardwareID")}
                                error={hasHardwareIDError}
                                data-testid="test-hardware-id"
                                maxLength={255}
                            />
                            {errorState.showHardwareIDError && (
                                <ControlError showIcon>{t(LOCALE_CONST.requiredField)}</ControlError>
                            )}
                            {errorState.showValidHardwareIDError && (
                                <ControlError showIcon>{t(LOCALE_CONST.hardwareIDValidation)}</ControlError>
                            )}
                        </Control>

                        {showPublicKey && (
                            <Control className="w-[50%]">
                                <Label className="font-normal" required requiredText="">
                                    {t(keyFieldLabel)}
                                </Label>
                                <Input
                                    placeholder="#######"
                                    size="regular"
                                    id={keyFieldType}
                                    value={hardwareData[keyFieldType] || ""}
                                    onChange={(e) => onInputChange(e, keyFieldType)}
                                    error={!!(keyFieldType === "encryptionKey" ? errorState.showEncryptionKeyError : errorState.showPublicKeyError)}
                                    data-testid="test-public-key"
                                    maxLength={2048}
                                />
                                {(keyFieldType === "encryptionKey" ? errorState.showEncryptionKeyError : errorState.showPublicKeyError) && (
                                    <ControlError showIcon>{t(LOCALE_CONST.requiredField)}</ControlError>
                                )}
                            </Control>
                        )}
                    </CardContent>
                }
            </Card>
        </>
    )
}

export default HardwareDetails;

